import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ROUTES } from '../../routes/path';
import { useLanguage } from '../../utils/LanguageContext';
import { getLocalized } from '../../utils/languageUtils';

export default function OrderConfirmation() {
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [orderData, setOrderData] = useState(null);

    useEffect(() => {
        const savedOrder = sessionStorage.getItem('lastOrder');
        if (!savedOrder) {
            navigate(`/${ROUTES.SHOP}`);
            return;
        }
        setOrderData(JSON.parse(savedOrder));
    }, [navigate]);

    if (!orderData) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <div style={{ fontSize: '18px', color: 'var(--text-light)' }}>Loading...</div>
        </div>
    );

    const totalTrees = orderData.cart?.reduce((sum, item) => sum + item.count, 0) || 0;

    return (
        <div style={{ maxWidth: '900px', margin: '120px auto', padding: '0 20px', textAlign: 'center' }}>
            {/* Success Icon */}
            <div style={{ width: '120px', height: '120px', background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%)', borderRadius: '50%', margin: '0 auto 30px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 30px rgba(31, 122, 69, 0.3)' }}>
                <div style={{ fontSize: '60px', color: 'white', fontWeight: 'bold' }}>✓</div>
            </div>

            <h1 style={{ fontSize: '42px', fontWeight: '800', marginBottom: '12px', color: 'var(--forest-900)' }}>{t('order_confirmed')}</h1>
            <p style={{ fontSize: '18px', color: 'var(--text-light)', marginBottom: '40px' }}>
                {t('order_thank_you')}, {orderData.userInfo?.fullName}
            </p>

            {/* Order Details Card */}
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: '24px', padding: '40px', textAlign: 'left', marginBottom: '40px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                {/* Order ID and Date */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '4px' }}>{t('order_id')}</div>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary)', fontFamily: 'monospace' }}>{orderData.orderId}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '4px' }}>{t('order_date')}</div>
                        <div style={{ fontSize: '16px', fontWeight: '600' }}>{new Date(orderData.orderDate).toLocaleDateString()}</div>
                    </div>
                </div>

                <div style={{ height: '1px', background: 'var(--border)', margin: '24px 0' }}></div>

                {/* Customer Info */}
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>🌱</span> {t('check_info_title')}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    <div>
                        <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '4px' }}>{t('check_label_name')}:</div>
                        <div style={{ fontWeight: '600' }}>{orderData.userInfo?.fullName}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '4px' }}>{t('check_label_phone')}:</div>
                        <div style={{ fontWeight: '600' }}>{orderData.userInfo?.phone}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '4px' }}>{t('check_label_region')}:</div>
                        <div style={{ fontWeight: '600' }}>{orderData.userInfo?.region}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '4px' }}>{t('check_payment_title')}:</div>
                        <div style={{ fontWeight: '600', textTransform: 'capitalize' }}>{orderData.paymentMethod}</div>
                    </div>
                </div>

                <div style={{ height: '1px', background: 'var(--border)', margin: '24px 0' }}></div>

                {/* Trees Ordered */}
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>🌳</span> {t('prof_trees')} ({totalTrees})
                </h3>
                {orderData.cart?.map((item, idx) => {
                    const itemPrice = parseFloat(item.product.price) || 0;
                    const itemDiscount = parseFloat(item.product.discount) || 0;
                    const itemSubtotal = itemPrice * item.count;
                    const itemDiscountAmount = (itemSubtotal * itemDiscount) / 100;
                    const itemTotal = itemSubtotal - itemDiscountAmount;

                    return (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: 'var(--bg)', borderRadius: '12px', marginBottom: '12px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
                                {item.product?.tree?.image ? (
                                    <img src={item.product.tree.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ width: '100%', height: '100%', background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>🌳</div>
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', fontSize: '15px' }}>
                                    {getLocalized(item.product.tree, language, 'name')}
                                </div>
                                <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                                    × {item.count} {item.count === 1 ? 'tree' : 'trees'}
                                </div>
                            </div>
                            <div style={{ fontWeight: '700', color: 'var(--primary)', fontSize: '16px' }}>
                                {itemTotal.toLocaleString()} UZS
                            </div>
                        </div>
                    );
                })}

                <div style={{ height: '1px', background: 'var(--border)', margin: '24px 0' }}></div>

                {/* Price Summary */}
                <div style={{ background: 'var(--bg)', padding: '20px', borderRadius: '12px', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>{t('cart_summary_title')}</h3>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' }}>
                        <span style={{ color: 'var(--text-light)' }}>{t('cart_subtotal')}</span>
                        <span style={{ fontWeight: '600' }}>{orderData.subtotal.toLocaleString()} UZS</span>
                    </div>

                    {orderData.totalDiscount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px', color: 'var(--danger)' }}>
                            <span>{t('cart_discount')}</span>
                            <span style={{ fontWeight: '600' }}>-{orderData.totalDiscount.toLocaleString()} UZS</span>
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' }}>
                        <span style={{ color: 'var(--text-light)' }}>{t('cart_service')}</span>
                        <span style={{ fontWeight: '600', color: 'var(--primary)' }}>{t('cart_free')}</span>
                    </div>

                    <div style={{ height: '1px', background: 'var(--border)', margin: '16px 0' }}></div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: '800' }}>
                        <span>{t('cart_total')}</span>
                        <span style={{ color: 'var(--primary)' }}>{orderData.total.toLocaleString()} UZS</span>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginBottom: '40px', flexWrap: 'wrap' }}>
                <Link
                    to={`/${ROUTES.SHOP}`}
                    style={{
                        padding: '14px 32px',
                        borderRadius: '12px',
                        fontWeight: '700',
                        textDecoration: 'none',
                        background: 'white',
                        color: 'var(--forest-900)',
                        border: '2px solid var(--border)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--primary)';
                        e.currentTarget.style.color = 'var(--primary)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border)';
                        e.currentTarget.style.color = 'var(--forest-900)';
                    }}
                >
                    <i className="fas fa-arrow-left"></i>
                    {t('cart_continue')}
                </Link>
                <Link
                    to={ROUTES.HOME}
                    style={{
                        padding: '14px 32px',
                        borderRadius: '12px',
                        fontWeight: '700',
                        textDecoration: 'none',
                        background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%)',
                        color: 'white',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 14px rgba(31, 122, 69, 0.4)',
                        transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(31, 122, 69, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(31, 122, 69, 0.4)';
                    }}
                >
                    <i className="fas fa-home"></i>
                    {t('order_btn_home')}
                </Link>
            </div>

            {/* Environmental Impact Badge */}
            <div style={{
                background: 'linear-gradient(135deg, var(--primary-tint) 0%, var(--primary-light) 100%)',
                border: '2px solid var(--primary)',
                borderRadius: '16px',
                padding: '24px 30px',
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                maxWidth: '600px',
                margin: '0 auto',
                boxShadow: '0 4px 6px rgba(31, 122, 69, 0.1)'
            }}>
                <div style={{ fontSize: '48px', flexShrink: 0 }}>🌍</div>
                <div style={{ textAlign: 'left', fontSize: '15px', lineHeight: '1.6' }}>
                    <strong style={{ color: 'var(--forest-700)' }}>{t('order_impact_text')}</strong>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--primary)', marginTop: '4px' }}>
                        {(totalTrees * 20).toLocaleString()} kg CO₂
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-light)', marginTop: '4px' }}>
                        Over the lifetime of your trees
                    </div>
                </div>
            </div>
        </div>
    );
}